import { useRef, useState } from 'react'
import clsx from 'clsx'
import { Element } from 'html-react-parser'
import dynamic from 'next/dynamic'
import styled, { useTheme } from 'styled-components'
import { ProductBannerScheme, ProductCategoryScheme, SupplyTypeEnum } from '@/apis/rest/generated/types'
import { PinchZoomWrapper, SanitizedHtmlDisplay } from '@/components/common/etc'
import FlatIcon from '@/components/common/icons/FlatIcon'
import { Image } from '@/components/common/images/Image'
import { Typo, TypoVariant } from '@/components/common/typography'
import styles from '@/components/domains/product-detail/product-detail-description.module.css'
import { IconNameEnum } from '@/constants/icon-name.enum'
import ROUTES from '@/constants/legacy/constRoutes'
import { useProductDetailEventTracker } from '@/containers/event-tracker/product-detail.event-tracker-new'
import { useCustomRouter } from '@/containers/hooks'
import { createFullURL } from '@/utils/utilURL'

interface ProductDetailDescriptionProps {
  description: string
  brandId: number
  brandName: string
  productId: number
  productName: string
  isRealSoldOut: boolean
  price: number
  discountRate: number
  salePrice: number
  reviewScore: number
  reviewCount: number
  isTimeSale: boolean
  categories: ProductCategoryScheme[]
  supplyType: SupplyTypeEnum
  onClickFoldButton: () => void
  productBanners?: ProductBannerScheme[]
}

const ProductDetailDescription = ({
  description,
  onClickFoldButton,
  brandId,
  brandName,
  productId,
  productName,
  isRealSoldOut,
  price,
  discountRate,
  salePrice,
  reviewScore,
  reviewCount,
  isTimeSale,
  categories,
  supplyType,
  productBanners = [],
}: ProductDetailDescriptionProps) => {
  const { color } = useTheme()
  const ableLazyLoadRef = useRef<boolean>(false)
  const [isFold, setIsFold] = useState<boolean>(true)
  const { trackClickProductDetailMoreInfo } = useProductDetailEventTracker()
  const { push } = useCustomRouter()

  const handleClickToggle = () => {
    setIsFold((prev) => !prev)
    if (!isFold) {
      onClickFoldButton()
    } else {
      trackClickProductDetailMoreInfo({
        brandId,
        brandName,
        productId,
        productName,
        isRealSoldOut,
        price,
        discountRate,
        salePrice,
        reviewScore,
        reviewCount,
        isTimeSale,
        categories,
        supplyType,
      })
    }
  }

  const handleClickBanner = (banner: ProductBannerScheme) => {
    if (banner.link) {
      push(ROUTES.EVENT_DETAIL.replace('[adSlug]', banner.link))
    }
  }

  return (
    <ProductDetailDescriptionWrapper className="product-detail-description" isFold={isFold}>
      <div className="product-detail-description__content">
        <div className="product-detail-description__intro-banners">
          {productBanners.map((banner) => (
            <Image
              className={clsx(styles['product-detail-description__intro-banner'], {
                [styles['cursor-pointer']]: !!banner.link,
              })}
              key={banner.image}
              src={createFullURL(banner.image)}
              alt="상품 상세 인트로 배너"
              onClick={() => handleClickBanner(banner)}
            />
          ))}
        </div>
        <PinchZoomWrapper>
          <SanitizedHtmlDisplay
            dirtyHtmlString={description}
            replaceParseHtml={(domNode) => {
              const { type, name, attribs } = domNode as Element
              if (type === 'tag' && name === 'img' && ableLazyLoadRef.current) {
                const alt = attribs?.alt || ''
                return <img data-src={attribs.src} className={'lazy-img detail-image-container'} alt={alt} />
              }
              ableLazyLoadRef.current = true
            }}
          />
          <div>&nbsp;</div>
        </PinchZoomWrapper>
      </div>
      <div className="product-detail-description__footer">
        <button type="button" className="product-detail-description__fold-button" onClick={handleClickToggle}>
          {isFold ? (
            <>
              <Typo variant={TypoVariant.Body1Medium} color={color.gray900}>
                상세정보 펼쳐보기
              </Typo>
              <FlatIcon type={IconNameEnum.IcArrowDown} color={color.gray900} size={'2rem'} />
            </>
          ) : (
            <>
              <Typo variant={TypoVariant.Body1Medium} color={color.gray900}>
                상세정보 접기
              </Typo>
              <FlatIcon type={IconNameEnum.IcArrowUp} color={color.gray900} size={'2rem'} />
            </>
          )}
        </button>
      </div>
    </ProductDetailDescriptionWrapper>
  )
}

const ProductDetailDescriptionWrapper = styled.div<{ isFold: boolean }>`
  position: relative;
  height: ${({ isFold }) => (isFold ? '80vh' : 'auto')};
  overflow: hidden;
  padding: 1.6rem 0;

  .product-detail-description {
    &__intro-banners {
      display: flex;
      flex-direction: column;
      width: 100%;
    }

    &__intro-banner {
      width: 100%;
      height: 100%;
    }

    &__footer {
      position: ${({ isFold }) => (isFold ? 'absolute' : 'relative')};
      bottom: 0;
      height: auto;
      background: ${({ isFold }) =>
        isFold
          ? 'linear-gradient(0.03deg, #FFFFFF 0%, rgba(255, 255, 255, 0.984375) 50%, rgba(255, 255, 255, 0) 100%)'
          : ''};
      width: 100%;
      padding: 11.2rem 1.6rem 4rem;
      padding-top: ${({ isFold }) => (isFold ? '11.2rem' : '2rem')};
    }
    &__fold-button {
      width: 100%;
      display: block;
      border-radius: 0.8rem;
      border: 0.1rem solid
        ${({
          theme: {
            color: { gray200 },
          },
        }) => gray200};
      padding: 1.3rem 1.4rem 1.3rem 1.6rem;
      cursor: pointer;
      background-color: ${({
        theme: {
          color: { grayWhite },
        },
      }) => grayWhite};
      display: flex;
      justify-content: center;
      align-items: center;
      gap: 0.4rem;
    }
  }
`

export const ProductDetailDescriptionDynamic = dynamic(() => Promise.resolve(ProductDetailDescription), { ssr: false })
